import noop from 'lodash/noop'
import { forwardRef } from 'react'
import { TextareaAutosize } from '@mui/base'
import { styled as muiStyled } from '@mui/system'
import styled from 'styled-components'
import { colors, transitions, fontSizes, fontWeights } from '@/styles'
import { toUnit } from '@/utils/styles'

const Control = muiStyled(props => <TextareaAutosize {...props} />)`
    position: relative;
    display: block;
    border-radius: 6px;
    padding: 8px 8px 80px 8px;
    width: 100%;
    max-height: ${props => (props.maxHeight ? toUnit(props.maxHeight) : '100px')};
    min-height: ${props => (props.minHeight ? toUnit(props.minHeight) : '100px')};
    border-radius: ${props => (props.radius ? toUnit(props.radius) : '6px')};
    border: ${props =>
      props.border
        ? props.border
        : props.showErrorBorder
        ? `1.5px solid ${colors.red64}`
        : `1.5px solid ${colors.grey88}`};
    font-size: ${props => (props.fontSize ? `${props.fontSize}px` : fontSizes.regular)};
    font-weight: ${props => props.fontWeight || fontWeights.regular};
    outline: none;
    color: ${colors.black};
    background: ${props => props.background || colors.white};
    transition: ${transitions.borderColor};
    resize: none;
    overflow: scroll !important;
    &::placeholder {
      color: ${colors.grey56};
      font-weight: ${fontWeights.regular};
    }
    &:focus {
      border-color: ${props => (props.showErrorBorder ? colors.red64 : colors.secondary)};
    }
    &:disabled {
      opacity: 0.3;
    }
  `

const Counter = styled.small`
  position: absolute;
  color: ${props => (props.showError ? colors.red64 : colors.black)};
  font-size: ${fontSizes.small};
  line-height: 22px;
  text-align: right;
  right: 5px;
  bottom: 0px;
`

const CustomBottomBar = styled.div`
  position: absolute;
  bottom: 1.5px;
  left: 1.5px;
  right: 1.5px;
  padding: 10px 10px 10px 10px;
  background: ${colors.white};
  box-sizing: border-box;
  overflow: visible;
  border-radius: 0 0 6px 6px;
`

const Wrapper = styled.div`
  height: auto;
  width: 100%;
  max-width: 100%;
  max-height: ${props => (props.maxHeight ? toUnit(props.maxHeight) : '100%')};
  min-height: ${props => (props.minHeight ? toUnit(props.minHeight) : '100%')};
  position: relative;
  margin-top: ${props => props.top || 0}px;
  overflow: auto;
  display: inline-block;
  & ${Control}:focus + ${CustomBottomBar} {
    border-color: ${props => (props.showErrorBorder ? colors.red64 : colors.secondary)};
  }
`

const Textarea = forwardRef(
  (
    {
      onChange,
      children,
      maxLength,
      border,
      value = '',
      setTextArea = noop,
      showErrorBorder,
      showCustomBar = false,
      BottomBarComponent = null,
      ...rest
    },
    ref
  ) => {
    const handleChange = e => {
      if (maxLength) e.target.value = e.target.value.slice(0, maxLength)
      onChange(e)
    }

    return (
      <Wrapper
        {...rest}
        ref={ref}
        top={rest.top}
        height={rest.height}
        background={rest.background}
        showErrorBorder={showErrorBorder}
      >
        <Control
          {...rest}
          showErrorBorder={showErrorBorder}
          value={value}
          border={border}
          onChange={handleChange}
          ref={textRef => setTextArea(textRef)}
        >
          {children}
        </Control>
        {showCustomBar ? (
          <CustomBottomBar border={border} showErrorBorder={showErrorBorder}>
            {BottomBarComponent && <BottomBarComponent />}
            {maxLength && (
              <Counter showError={showErrorBorder}>
                {value?.length}/{maxLength}
              </Counter>
            )}
          </CustomBottomBar>
        ) : (
          maxLength && (
            <Counter showError={showErrorBorder}>
              {value?.length}/{maxLength}
            </Counter>
          )
        )}
      </Wrapper>
    )
  }
)

Textarea.displayName = 'Textarea'

export default Textarea
